import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWS,
  REPORT_PAGE_HEIGHT,
} from '../PageConfigProvider/constants';
import type { TabContent } from '../PageConfigProvider/page-reducer';
import type { PositionedItem } from '../PageConfigProvider/types/item';
import {
  blockItems,
  REPORT_FLOW_MIN_ITEM_HEIGHT_ROWS,
} from './report-flow-reducer';
import type {
  ReportFlowBlock,
  ReportFlowColumnsBlock,
  ReportFlowDocument,
  ReportFlowLayout,
  ReportFlowLayoutBlock,
  ReportFlowLeafBlock,
  ReportFlowPage,
  ReportFlowSection,
  ReportFlowTocChild,
  ReportFlowTocEntry,
} from './report-flow-types';

export const REPORT_FLOW_ROW_PX = REPORT_PAGE_HEIGHT / DEFAULT_GRID_ROWS;
const CHART_MIN_HEIGHT_PX = Math.round(
  REPORT_FLOW_MIN_ITEM_HEIGHT_ROWS * REPORT_FLOW_ROW_PX
);
const CHART_MAX_HEIGHT_PX = 1000;

const blockAnchorId = (blockId: string) => `rf-block-${blockId}`;
const headingAnchorId = (blockId: string, index: number) =>
  `rf-${blockId}-h${index}`;
const sectionAnchorId = (sectionId: string) => `rf-section-${sectionId}`;

const isLayoutBlockRef = (ref: unknown): ref is ReportFlowLayoutBlock => {
  if (typeof ref !== 'object' || ref === null) return false;
  const candidate = ref as {
    kind?: unknown;
    itemId?: unknown;
    itemIds?: unknown;
  };
  if (candidate.kind === 'item') return typeof candidate.itemId === 'string';
  if (candidate.kind === 'columns') {
    return (
      Array.isArray(candidate.itemIds) &&
      candidate.itemIds.every((itemId) => typeof itemId === 'string')
    );
  }
  return false;
};

const isReportFlowLayout = (layout: unknown): layout is ReportFlowLayout => {
  if (typeof layout !== 'object' || layout === null) return false;
  const candidate = layout as { pages?: unknown };
  return (
    Array.isArray(candidate.pages) &&
    candidate.pages.every((page) => {
      if (typeof page !== 'object' || page === null) return false;
      const layoutPage = page as { id?: unknown; blocks?: unknown };
      return (
        typeof layoutPage.id === 'string' &&
        Array.isArray(layoutPage.blocks) &&
        layoutPage.blocks.every(isLayoutBlockRef)
      );
    })
  );
};

const stripHandlebars = (text: string) =>
  text
    .replace(/\{\{[^}]*\}\}/g, '')
    .replace(/\s+/g, ' ')
    .trim();

export const isHtmlContent = (source: string) =>
  source.trimStart().startsWith('<');

type ExtractedHeading = { level: number; text: string };

const extractHtmlHeadings = (html: string): ExtractedHeading[] => {
  if (typeof DOMParser === 'undefined') return [];
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return Array.from(doc.body.querySelectorAll('h1, h2, h3, h4')).map((el) => ({
    level: Number(el.tagName.charAt(1)),
    text: (el.textContent ?? '').trim(),
  }));
};

const extractMarkdownHeadings = (markdown: string): ExtractedHeading[] => {
  const headings: ExtractedHeading[] = [];
  for (const line of markdown.split('\n')) {
    const match = line.match(/^(#{1,4})\s+(.*)$/);
    const [, hashes, text] = match ?? [];
    if (hashes !== undefined && text !== undefined) {
      headings.push({ level: hashes.length, text: text.trim() });
    }
  }
  return headings;
};

const extractHeadings = (source: string): ExtractedHeading[] =>
  isHtmlContent(source)
    ? extractHtmlHeadings(source)
    : extractMarkdownHeadings(source);

const extractHtmlBodyText = (html: string): string => {
  if (typeof DOMParser === 'undefined') return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.body.querySelectorAll('h1, h2, h3, h4').forEach((el) => {
    el.remove();
  });
  return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim();
};

const extractMarkdownBodyText = (markdown: string): string =>
  markdown
    .split('\n')
    .filter((line) => !/^#{1,6}\s/.test(line))
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();

const textSource = (item: PositionedItem): string => item.plot?.textHtml ?? '';

const htmlNodeToMarkdown = (node: Node): string => {
  if (node.nodeType === Node.TEXT_NODE) {
    return (node.textContent ?? '').replace(/\s+/g, ' ');
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return '';

  const element = node as Element;
  const childText = () =>
    Array.from(element.childNodes).map(htmlNodeToMarkdown).join('');

  const tag = element.tagName.toLowerCase();
  const headingMatch = tag.match(/^h([1-6])$/);
  if (headingMatch) {
    return `\n\n${'#'.repeat(Number(headingMatch[1]))} ${childText().trim()}\n\n`;
  }

  switch (tag) {
    case 'p':
    case 'div':
      return `\n\n${childText().trim()}\n\n`;
    case 'br':
      return '\n';
    case 'ul':
    case 'ol': {
      const items = Array.from(element.children)
        .filter((child) => child.tagName.toLowerCase() === 'li')
        .map((li, index) => {
          const marker = tag === 'ol' ? `${index + 1}.` : '-';
          return `${marker} ${Array.from(li.childNodes)
            .map(htmlNodeToMarkdown)
            .join('')
            .trim()}`;
        });
      return `\n\n${items.join('\n')}\n\n`;
    }
    case 'strong':
    case 'b': {
      const inner = childText().trim();
      return inner ? `**${inner}**` : '';
    }
    case 'em':
    case 'i': {
      const inner = childText().trim();
      return inner ? `*${inner}*` : '';
    }
    case 'a': {
      const href = element.getAttribute('href');
      const label = childText().trim();
      return href ? `[${label}](${href})` : label;
    }
    default:
      return childText();
  }
};

export const htmlToMarkdown = (html: string): string => {
  if (typeof DOMParser === 'undefined') return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return Array.from(doc.body.childNodes)
    .map(htmlNodeToMarkdown)
    .join('')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const toRenderableTextItem = (item: PositionedItem): PositionedItem => {
  const source = textSource(item);
  if (!source || !isHtmlContent(source)) return item;
  return {
    ...item,
    plot: { ...item.plot, textHtml: htmlToMarkdown(source) },
  };
};

const itemHeadings = (item: PositionedItem): ExtractedHeading[] => {
  if (item.plot?.chartType !== 'text') return [];
  const source = textSource(item);
  return source ? extractHeadings(source) : [];
};

const extractCoverCopy = (
  coverItems: PositionedItem[]
): { title?: string; description?: string } => {
  const sorted = [...coverItems].sort(
    (a, b) => a.position.y - b.position.y || a.position.x - b.position.x
  );
  for (const item of sorted) {
    if (item.plot?.chartType !== 'text') continue;
    const source = textSource(item);
    if (!source) continue;
    const headings = extractHeadings(source);
    const title = headings[0] ? stripHandlebars(headings[0].text) : undefined;
    const body = isHtmlContent(source)
      ? extractHtmlBodyText(source)
      : extractMarkdownBodyText(source);
    const description = stripHandlebars(body) || undefined;
    if (title || description) return { title, description };
  }
  return {};
};

const leafKind = (item: PositionedItem): ReportFlowLeafBlock['kind'] => {
  const chartType = item.plot?.chartType;
  if (chartType === 'text') return 'text';
  if (chartType === 'image') return 'image';
  return 'chart';
};

const leafHeightPx = (item: PositionedItem): number | undefined => {
  const kind = leafKind(item);
  if (kind === 'text') return undefined;
  const gridHeight = Math.round(item.position.height * REPORT_FLOW_ROW_PX);
  if (kind === 'image') return gridHeight;
  return Math.min(
    CHART_MAX_HEIGHT_PX,
    Math.max(CHART_MIN_HEIGHT_PX, gridHeight)
  );
};

const leafWidthPct = (item: PositionedItem): number | undefined => {
  if (leafKind(item) === 'text') return undefined;
  const cols = item.position.width;
  if (cols >= DEFAULT_GRID_COLUMNS) return undefined;
  return Math.round((cols / DEFAULT_GRID_COLUMNS) * 1000) / 10;
};

const buildLeafBlock = (item: PositionedItem): ReportFlowLeafBlock => {
  const kind = leafKind(item);
  return {
    kind,
    id: item.id,
    anchorId: blockAnchorId(item.id),
    item: kind === 'text' ? toRenderableTextItem(item) : item,
    heightPx: leafHeightPx(item),
    widthPct: leafWidthPct(item),
    align: item.align,
    headings: itemHeadings(item).map((heading, index) => ({
      level: heading.level,
      text: heading.text,
      anchorId: headingAnchorId(item.id, index),
    })),
  };
};

// Unchanged items keep their identity across edits, so text is parsed once per change.
const leafBlockCache = new WeakMap<PositionedItem, ReportFlowLeafBlock>();

const toLeafBlock = (item: PositionedItem): ReportFlowLeafBlock => {
  const cached = leafBlockCache.get(item);
  if (cached) return cached;
  const leaf = buildLeafBlock(item);
  leafBlockCache.set(item, leaf);
  return leaf;
};

const overlapsHorizontally = (a: PositionedItem, b: PositionedItem) =>
  a.position.x < b.position.x + b.position.width &&
  b.position.x < a.position.x + a.position.width;

const groupIntoBlocks = (items: PositionedItem[]): ReportFlowBlock[] => {
  const sorted = [...items].sort(
    (a, b) => a.position.y - b.position.y || a.position.x - b.position.x
  );

  const rows: PositionedItem[][] = [];
  let currentRow: PositionedItem[] = [];
  let currentRowEnd = Number.NEGATIVE_INFINITY;

  for (const item of sorted) {
    const { y, height } = item.position;
    // Join the row only when overlapping it vertically and clearing every member horizontally.
    const belongsToCurrentRow =
      currentRow.length > 0 &&
      y < currentRowEnd &&
      currentRow.every((rowItem) => !overlapsHorizontally(rowItem, item));

    if (belongsToCurrentRow) {
      currentRow.push(item);
      currentRowEnd = Math.max(currentRowEnd, y + height);
    } else {
      if (currentRow.length > 0) rows.push(currentRow);
      currentRow = [item];
      currentRowEnd = y + height;
    }
  }
  if (currentRow.length > 0) rows.push(currentRow);

  return rows.flatMap((row): ReportFlowBlock[] => {
    const first = row[0];
    if (!first) return [];
    if (row.length === 1) return [toLeafBlock(first)];
    const columns = [...row]
      .sort((a, b) => a.position.x - b.position.x)
      .map(toLeafBlock);
    return [buildColumnsBlock(columns)];
  });
};

const buildColumnsBlock = (
  leaves: ReportFlowLeafBlock[]
): ReportFlowColumnsBlock => {
  const id = `columns-${leaves[0]?.id ?? 'empty'}`;
  return {
    kind: 'columns',
    id,
    anchorId: blockAnchorId(id),
    columns: leaves,
  };
};

export const blockLeaves = (block: ReportFlowBlock): ReportFlowLeafBlock[] =>
  block.kind === 'columns' ? block.columns : [block];

/** The leaf whose leading heading names the section, including headings inside columns. */
export const sectionHeadingLeaf = (
  block: ReportFlowBlock
): ReportFlowLeafBlock | undefined =>
  blockLeaves(block).find((leaf) => {
    if (leaf.kind !== 'text') return false;
    const first = leaf.headings[0];
    return first !== undefined && first.level <= 2;
  });

const sectionTitleOf = (block: ReportFlowBlock): string | undefined => {
  const heading = sectionHeadingLeaf(block)?.headings[0];
  if (!heading) return undefined;
  return stripHandlebars(heading.text) || undefined;
};

const groupIntoSections = (blocks: ReportFlowBlock[]): ReportFlowSection[] => {
  const sections: ReportFlowSection[] = [];
  let current: ReportFlowSection | undefined;

  const startSection = (title: string | undefined, seed: ReportFlowBlock) => {
    const id = `section-${seed.id}`;
    current = { id, anchorId: sectionAnchorId(id), title, blocks: [seed] };
    sections.push(current);
  };

  for (const block of blocks) {
    const title = sectionTitleOf(block);
    if (title !== undefined || current === undefined) {
      startSection(title, block);
    } else {
      current.blocks.push(block);
    }
  }
  return sections;
};

const buildToc = (sections: ReportFlowSection[]): ReportFlowTocEntry[] => {
  const entries: ReportFlowTocEntry[] = [];

  for (const section of sections) {
    if (!section.title) continue;

    const children: ReportFlowTocChild[] = [];
    section.blocks.forEach((block, blockIndex) => {
      // Skip the leaf+heading that titled the section so it isn't repeated as a child.
      const titleLeaf =
        blockIndex === 0 ? sectionHeadingLeaf(block) : undefined;
      for (const leaf of blockLeaves(block)) {
        leaf.headings.forEach((heading, headingIndex) => {
          const isSectionHeading = leaf === titleLeaf && headingIndex === 0;
          if (isSectionHeading || heading.level > 3) return;
          const label = stripHandlebars(heading.text);
          if (label) children.push({ label, anchorId: heading.anchorId });
        });
      }
    });

    entries.push({
      label: section.title,
      anchorId: section.anchorId,
      sectionId: section.id,
      children,
    });
  }

  if (entries.length > 0) return entries;

  for (const section of sections) {
    for (const block of section.blocks) {
      for (const leaf of blockLeaves(block)) {
        if (leaf.kind !== 'chart' || !leaf.item.title) continue;
        const label = stripHandlebars(leaf.item.title);
        if (!label) continue;
        entries.push({
          label,
          anchorId: leaf.anchorId,
          sectionId: section.id,
          children: [],
        });
      }
    }
  }
  return entries;
};

/** The report cover is a header, not a section — it gets its own TOC entry. */
export const REPORT_FLOW_COVER_ANCHOR_ID = 'rf-cover';
export const REPORT_FLOW_COVER_SECTION_ID = 'rf-cover-section';

const withCoverEntry = (
  entries: ReportFlowTocEntry[],
  title: string
): ReportFlowTocEntry[] => {
  const label = stripHandlebars(title);
  if (!label) return entries;
  return [
    {
      label,
      anchorId: REPORT_FLOW_COVER_ANCHOR_ID,
      sectionId: REPORT_FLOW_COVER_SECTION_ID,
      children: [],
    },
    ...entries,
  ];
};

const formatCreatedLabel = (created?: string): string | undefined => {
  if (!created) return undefined;
  const date = new Date(created);
  if (Number.isNaN(date.getTime())) return undefined;
  return `Created ${date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })}`;
};

const partitionGridItems = (tabContent: TabContent) => {
  const items = tabContent.items ?? [];
  const gridRows = tabContent.grid?.rows || DEFAULT_GRID_ROWS;
  const numPages = Math.max(1, Math.floor(gridRows / DEFAULT_GRID_ROWS));

  const coverEndY = numPages >= 2 ? DEFAULT_GRID_ROWS : 0;
  const footerStartY =
    numPages >= 3
      ? (numPages - 1) * DEFAULT_GRID_ROWS
      : Number.POSITIVE_INFINITY;

  const coverItems = items.filter((item) => item.position.y < coverEndY);
  const contentItems = items.filter(
    (item) => item.position.y >= coverEndY && item.position.y < footerStartY
  );
  return { coverItems, contentItems };
};

const blockToLayoutRef = (block: ReportFlowBlock): ReportFlowLayoutBlock =>
  block.kind === 'columns'
    ? { kind: 'columns', itemIds: block.columns.map((leaf) => leaf.item.id) }
    : { kind: 'item', itemId: block.item.id };

const deriveReportFlowLayout = (tabContent: TabContent): ReportFlowLayout => {
  const { contentItems } = partitionGridItems(tabContent);

  const bands = new Map<number, PositionedItem[]>();
  for (const item of contentItems) {
    const band = Math.floor(item.position.y / DEFAULT_GRID_ROWS);
    const bandItems = bands.get(band) ?? [];
    bandItems.push(item);
    bands.set(band, bandItems);
  }

  const pages = [...bands.entries()]
    .sort(([a], [b]) => a - b)
    .map(([band, bandItems]) => ({
      id: `page-${band}`,
      blocks: groupIntoBlocks(bandItems).map(blockToLayoutRef),
    }));

  return {
    version: 1,
    pages: pages.length > 0 ? pages : [{ id: 'page-1', blocks: [] }],
  };
};

const resolveReportFlowLayout = (tabContent: TabContent): ReportFlowLayout =>
  isReportFlowLayout(tabContent.reportFlow) &&
  tabContent.reportFlow.pages.length > 0
    ? tabContent.reportFlow
    : deriveReportFlowLayout(tabContent);

/** Places the tab's items into nested pages, from its stored layout or the grid bands. */
export const nestedPagesFromTabContent = (
  tabContent: TabContent
): ReportFlowPage[] => {
  const itemById = new Map(
    (tabContent.items ?? []).map((item) => [item.id, item])
  );
  const lookup = (itemIds: string[]) =>
    itemIds.flatMap((itemId) => {
      const item = itemById.get(itemId);
      return item ? [item] : [];
    });
  return resolveReportFlowLayout(tabContent).pages.map((page) => ({
    id: page.id,
    blocks: page.blocks.flatMap((ref): ReportFlowPage['blocks'] => {
      const items = lookup(ref.kind === 'columns' ? ref.itemIds : [ref.itemId]);
      const [first] = items;
      if (first === undefined) return [];
      if (items.length === 1) return [{ kind: 'item', item: first }];
      return [{ kind: 'columns', items }];
    }),
  }));
};

export type ResolvedLayoutPage = { id: string; blocks: ReportFlowBlock[] };

const isConfiguredItem = (item: PositionedItem) =>
  item.plot?.chartType !== undefined;

/** The editor keeps unconfigured leaves (it hides them only when read-only); views pass `isConfiguredItem`. */
export const resolveLayoutPages = (
  pages: ReportFlowPage[],
  include: (item: PositionedItem) => boolean = () => true
): ResolvedLayoutPage[] =>
  pages.map((page) => ({
    id: page.id,
    blocks: page.blocks.flatMap((block): ReportFlowBlock[] => {
      const leaves = blockItems(block).filter(include).map(toLeafBlock);
      const [first] = leaves;
      if (first === undefined) return [];
      return [leaves.length === 1 ? first : buildColumnsBlock(leaves)];
    }),
  }));

const buildBlocksFromPages = (pages: ReportFlowPage[]): ReportFlowBlock[] =>
  resolveLayoutPages(pages, isConfiguredItem).flatMap((page, pageIndex) =>
    page.blocks.map((block, blockIndex) =>
      pageIndex > 0 && blockIndex === 0 ? { ...block, startsPage: true } : block
    )
  );

export const buildReportFlowDocument = ({
  title,
  description,
  created,
  pages,
}: {
  title: string;
  description?: string;
  created?: string;
  pages: ReportFlowPage[];
}): ReportFlowDocument => {
  const sections = groupIntoSections(buildBlocksFromPages(pages));
  return {
    title,
    description: description || undefined,
    createdLabel: formatCreatedLabel(created),
    sections,
    toc: withCoverEntry(buildToc(sections), title),
  };
};

export const extractLegacyCoverCopy = (
  tabContent: TabContent
): { title?: string; description?: string } => {
  const { coverItems } = partitionGridItems(tabContent);
  return extractCoverCopy(coverItems);
};
